import { useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Text, TextButton } from '@zillow/constellation-10';
import { stringHelper } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../../selectors/useSelector';

import propertyUtils from '../../../../utils/propertyUtils';

import RecommendationsLearnMoreContent from '../../../../components/RecommendationsLearnMoreContent';
import RecommendationIconBulb from '../../../../components/RecommendationIconBulb';
import { RECS, REVIEW_PAGE_SECTION_RECS } from '../../../../constants/RecommendationsConstants';
import { MODAL_COMPONENT } from '../../../../components/WizardModularListing/Review/constants';

import AccordionIconError from './AccordionIconError';
import ContentSectionEditButton from './ContentSectionEditButton';
import FieldValueDisplay from './FieldValueDisplay';

import {
  StyledIconPlaceholder,
  StyledReviewSection,
  StyledReviewSectionTitle,
  StyledReviewTitleRow,
} from '../../../../components/WizardModularListing/Review/styles';

import type { MouseEvent } from 'react';
import type { AmenitiesProps } from '../../../../model/Amenities';

const ACTION_NEEDED = 'Action needed';

const ESCAPE_VALUES = {
  'Ev ': 'EV ', // if EV is first option
  ' ev ': ' EV ', // if EV is trailing option
} as const;

const ariaLabels = {
  AMENITIES: 'amenities',
  LAUNDRY: 'laundry',
  INTERIOR_AMENITIES: 'interior-amenities',
  PROPERTY_AMENITIES: 'property-amenities',
} as const;

const getDisplayValue = ({ amenitiesToCompute }: { amenitiesToCompute: AmenitiesProps['property'] }) => {
  return amenitiesToCompute.reduce((acc: Array<JSX.Element>, { category, values, isRequired }) => {
    const displayValue = values
      .reduce((a, { displayName, isSelected }) => (!isSelected ? a : (a.push(displayName), a)), [] as Array<string>)
      .join(', ');

    if (displayValue || isRequired) {
      let finalDisplayValue = displayValue ? stringHelper.capitalize(displayValue.toLowerCase()) : '';

      if (finalDisplayValue) {
        Object.entries(ESCAPE_VALUES).forEach(([key, value]) => {
          finalDisplayValue = finalDisplayValue.replace(key, value);
        });
      }

      acc.push(
        <div key={category}>
          {category}: {finalDisplayValue || (isRequired && <Text color="text.subtle">{ACTION_NEEDED}</Text>)}
        </div>,
      );
    }

    return acc;
  }, [] as Array<JSX.Element>);
};

type Props = {
  onEditClick: (e: MouseEvent<HTMLButtonElement>) => void;
};

const AmenitiesContent = ({ onEditClick }: Props) => {
  const {
    canUpdateListing,
    recommendations,
    propertyAmenities,
    amenities: { interior, property },
    listingTypeCode,
    active,
    isFeed,
    isArchived,
    restrictions: { isUpdateListingRestricted },
  } = useSelector((state) => {
    return {
      canUpdateListing: state.user.features.canUpdateListing,
      recommendations: state.properties.recommendations,
      propertyAmenities: state.properties.currentProperty.propertyAmenities,
      amenities: state.properties.currentProperty.amenities,
      listingTypeCode: state.properties.currentProperty.listingTypeCode,
      active: state.properties.currentProperty.active,
      isFeed: state.properties.currentProperty.isFeed,
      isArchived: state.properties.currentProperty.isArchived,
      restrictions: state.properties.currentProperty.restrictions,
    };
  }, shallowEqual);

  const [displayRecsModal, setDisplayRecsModal] = useState('');

  const customNoneFieldValueText = isFeed ? 'None' : ACTION_NEEDED;

  const isDisabled = isFeed || !canUpdateListing || isArchived || isUpdateListingRestricted;

  const isInteriorRequirementFulfilled = propertyUtils.checkIsAmenityFieldFulfilled({
    amenityField: interior,
  });
  const renderAdditionalAmenities = () => {
    return propertyAmenities.map((item: { id: string; amenity: string }) => {
      const { id, amenity } = item;
      return <div key={id}>{amenity}</div>;
    });
  };

  const displayAdditionalAmenities = renderAdditionalAmenities();

  const displayInteriorAmenities = useMemo(() => getDisplayValue({ amenitiesToCompute: interior }), [interior]);
  const displayPropertyAmenities = useMemo(() => getDisplayValue({ amenitiesToCompute: property }), [property]);

  return (
    <div data-testid="AmenitiesContent" data-group-title="Amenities">
      <StyledReviewSection data-testid="InteriorAmenities">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            {!active && !isInteriorRequirementFulfilled && !isDisabled ? (
              <StyledIconPlaceholder>
                <AccordionIconError tabIndex={0} title="Laundry incomplete error" />
              </StyledIconPlaceholder>
            ) : (
              <StyledIconPlaceholder>
                {recommendations[REVIEW_PAGE_SECTION_RECS.AMENITIES][RECS.LAUNDRY] ? (
                  <RecommendationIconBulb
                    fieldName={ariaLabels.LAUNDRY}
                    removeFlex={true}
                    onClick={() => {
                      setDisplayRecsModal(RECS.LAUNDRY);
                    }}
                  />
                ) : recommendations[REVIEW_PAGE_SECTION_RECS.AMENITIES][RECS.AMENITIES] ? (
                  <RecommendationIconBulb
                    fieldName={ariaLabels.INTERIOR_AMENITIES}
                    removeFlex={true}
                    onClick={() => {
                      setDisplayRecsModal(RECS.AMENITIES);
                    }}
                  />
                ) : null}
              </StyledIconPlaceholder>
            )}
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Interior amenities</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.INTERIOR_AMENITIES}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(RECS.AMENITIES);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.INTERIOR_AMENITIES}
            value={MODAL_COMPONENT.AmenitiesInteriorFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay
          value={displayInteriorAmenities.length ? displayInteriorAmenities : customNoneFieldValueText}
        />
      </StyledReviewSection>

      <StyledReviewSection data-testid="PropertyAmenities">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder>
              {recommendations[REVIEW_PAGE_SECTION_RECS.AMENITIES][RECS.AMENITIES] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.PROPERTY_AMENITIES}
                  removeFlex={true}
                  onClick={() => {
                    setDisplayRecsModal(RECS.AMENITIES);
                  }}
                />
              )}
            </StyledIconPlaceholder>
            <StyledReviewSectionTitle>
              <Text textStyle="body-bold">Property amenities</Text>
              <TextButton
                aria-label={`learn-more-about-${ariaLabels.PROPERTY_AMENITIES}`}
                textStyle="body"
                onClick={() => {
                  setDisplayRecsModal(RECS.AMENITIES);
                }}
              >
                Learn more
              </TextButton>
            </StyledReviewSectionTitle>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.PROPERTY_AMENITIES}
            value={MODAL_COMPONENT.AmenitiesPropertyFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={displayPropertyAmenities.length ? displayPropertyAmenities : undefined} />
      </StyledReviewSection>

      <StyledReviewSection data-testid="Amenities">
        <StyledReviewTitleRow>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconPlaceholder />
            <Text textStyle="body-bold">Additional amenities</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.AMENITIES}
            value={MODAL_COMPONENT.AmenitiesAdditionalFormField.componentKey}
            onEditClick={onEditClick}
          />
        </StyledReviewTitleRow>

        <FieldValueDisplay value={displayAdditionalAmenities} />
      </StyledReviewSection>

      {displayRecsModal && (
        <RecommendationsLearnMoreContent
          onClose={() => setDisplayRecsModal('')}
          recommendationType={displayRecsModal}
        />
      )}
    </div>
  );
};

export default AmenitiesContent;
